import './StoryViewer.sass';

import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { StoryViewerProps } from './StoryViewer.interface';
import { Story } from '$shared/components/Story/Story';
import { storiesActions, storiesSelector } from '$store/stories';
import { hideModal, showModal } from '$navigation/helper';
import { StoryModel } from '$store/models';
import { Loader } from '$shared/components';
import uniqBy from 'lodash/uniqBy';
import { StoryTape } from '$core/StoryViewer/StoryTape';
import { IcAngleLeft28, IcAngleRight28 } from '$assets';
import { useDesktop } from '$hooks';

export const StoryViewer: FC<StoryViewerProps> = (props) => {
  const RATE = 16 / 9;
  const { userId, storyId } = props;
  const isDesktop = useDesktop();
  const { stories, isLoading } = useSelector(storiesSelector);
  const dispatch = useDispatch();
  const [screen, setScreen] = useState([window.innerWidth, window.innerHeight]);
  const { route } = useRoute();
  const unreadOnlyMode = route.meta?.options?.unreadOnlyMode;

  const [width, height] = useMemo(() => {
    const [width, height] = screen;
    return width > height ? [height / RATE, height] : screen;
  }, [screen]);

  const users = useMemo(() => {
    if (unreadOnlyMode) {
      return stories.filter((user) => user.stories.some((s) => s.unread));
    }
    return stories;
  }, [unreadOnlyMode, isLoading]);

  const storiesFlatList = useMemo(() => {
    const stories: StoryModel[] = [];
    users.forEach((user) =>
      user.stories.forEach((story) => {
        stories.push(story);
      }),
    );
    return stories;
  }, [users]);

  const currentIndex = useMemo(() => {
    return storiesFlatList.findIndex((s) => s.userId === userId && s.id === storyId);
  }, [storiesFlatList, userId, storyId]);

  const currentUserIndex = useMemo(() => {
    return users.findIndex((user) => user.userId === userId);
  }, [users, userId]);

  const currentStory = useMemo(
    () => storiesFlatList[currentIndex],
    [storiesFlatList, currentIndex],
  );

  useEffect(() => {
    if (!isLoading && !currentStory && !unreadOnlyMode) {
      dispatch(
        storiesActions.getStories({
          userId,
          storyId,
        }),
      );
    }
  }, [currentStory, isLoading, userId, storyId, unreadOnlyMode]);

  const preloadStories = useMemo(() => {
    return uniqBy(
      [
        ...storiesFlatList.slice(currentIndex, currentIndex + 4),
        users[currentUserIndex + 1]?.stories[0],
      ].filter(Boolean),
      (s) => s.mediaUrl,
    );
  }, [storiesFlatList, users, currentIndex, currentUserIndex]);

  useEffect(() => {
    dispatch(
      storiesActions.readOne({
        storyId,
        duration: 1,
      }),
    );
  }, [storyId]);

  const getStory = useCallback(
    (offset = 0) => {
      if (unreadOnlyMode && offset > 0) {
        return storiesFlatList.slice(currentIndex + offset).find((s) => s.unread);
      } else {
        return storiesFlatList[currentIndex + offset];
      }
    },
    [storiesFlatList, currentIndex, unreadOnlyMode],
  );

  const setStory = useCallback(
    (offset) => {
      const story = getStory(offset);
      if (story) {
        showModal(`story${story.userId}_${story.id}`, true);
      } else {
        if (offset > 0) {
          hideModal();
        } else {
          dispatchEvent(new CustomEvent('storyGoTo', { detail: 0 }));
        }
      }
    },
    [storiesFlatList, currentIndex, getStory],
  );

  const setUser = useCallback(
    (id = 0) => {
      console.log(id);
      const user = users[id];
      if (user) {
        const storyId = unreadOnlyMode
          ? user.stories.find((s) => s.unread)?.id
          : user.stories[0].id;
        showModal(`story${user.userId}_${storyId}`, true);
      } else {
        hideModal();
      }
    },
    [users, currentUserIndex, unreadOnlyMode],
  );

  useEffect(() => {
    const handleUserKeyPress = ({ key }) => {
      if (key === 'ArrowLeft') {
        setStory(-1);
      } else if (key === 'ArrowRight') {
        setStory(1);
      }
    };

    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [setStory]);

  useEffect(() => {
    const handleResize = () => {
      setScreen([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setScreen]);

  useEffect(() => {
    return () => {
      dispatchEvent(new CustomEvent('storiesListScrollToUser', { detail: userId }));
    };
  }, [props, dispatch]);

  const handleClose = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      hideModal();
    }
  }, []);

  if (!currentStory) {
    return (
      <div className="StoryViewer__screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="StoryViewer__screen" onPointerDown={handleClose}>
      {currentStory ? (
        <div
          className="StoryViewer"
          style={{
            width: Math.ceil(width) + 'px',
            height: Math.ceil(height) + 'px',
          }}
        >
          {isDesktop && storiesFlatList[currentIndex - 1] && (
            <div onClick={() => setStory(-1)} className="StoryViewer__button prev">
              <IcAngleLeft28 />
            </div>
          )}
          {isDesktop && storiesFlatList[currentIndex + 1] && (
            <div onClick={() => setStory(1)} className="StoryViewer__button next">
              <IcAngleRight28 />
            </div>
          )}
          <StoryTape
            onChangeStory={setStory}
            onChangeUser={setUser}
            onClose={hideModal}
            width={width}
            height={height}
            storyIndex={currentUserIndex}
          >
            {users.map((user) => (
              <Story
                key={user.userId}
                id={user.userId === userId ? storyId : undefined}
                stories={user.stories}
                userId={user.userId}
                onEnd={user.userId === userId ? () => setStory(1) : undefined}
                simple={user.userId !== userId}
              />
            ))}
          </StoryTape>
          <div className="StoryViewer__preloader">
            {preloadStories.map((story) =>
              story.type === 'video' ? (
                <video
                  key={story.mediaUrl}
                  controls={false}
                  preload="auto"
                  disablePictureInPicture
                  playsInline
                >
                  <source src={story.mediaUrl} type="video/mp4,video/quicktime" />
                </video>
              ) : (
                <img key={story.mediaUrl} alt={story.mediaUrl} src={story.mediaUrl} />
              ),
            )}
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
};
